/**
 * Helpers for the installed (Add to Home Screen) version of the app.
 *
 * "Standalone" means the app runs without browser chrome. That changes a few
 * assumptions we can otherwise make: there is no address bar to fall back on,
 * `window.open` leaves the app context (on iOS it hands the user over to Safari),
 * and the viewport reaches into the status bar and home indicator areas.
 */

/** True when the app was launched from the home screen instead of a browser tab. */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  // iOS Safari predates display-mode and exposes navigator.standalone instead.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return (
    iosStandalone === true ||
    ['standalone', 'minimal-ui', 'fullscreen'].some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
    )
  )
}

export const isIos = (): boolean =>
  typeof window !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac, the touch points give it away
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1))

export const isSafari = (): boolean =>
  typeof window !== 'undefined' &&
  /^((?!chrome|android|crios|fxios).)*safari/i.test(window.navigator.userAgent)

/**
 * iOS only offers "Add to Home Screen" from Safari's share sheet, and only Safari.
 * Chrome/Firefox on iOS cannot install a PWA at all, so we should not tell people to try.
 */
export const canInstallOnIos = (): boolean => isIos() && isSafari() && !isStandalone()
